import React from "react";
import { ArrowRight } from "lucide-react";
import { Reveal } from "@/components/site/primitives";
import { INTENTS, PHOTO_CREDIT } from "@/content/worldFarmers";
import { scrollToId } from "@/lib/scrollTo";

const TARGET = { "where-to-buy": "events", support: "get-involved", "who-we-are": "about" };

const Panel = ({ intent }) => {
  const go = (e) => { e.preventDefault(); scrollToId(TARGET[intent.id] || intent.id); };
  return (
    <article
      id={intent.id}
      data-testid={`intent-card-${intent.id}`}
      className="group relative min-h-[420px] overflow-hidden rounded-sm lg:min-h-[68vh] lg:grow lg:basis-0 lg:transition-[flex-grow] lg:duration-500 lg:ease-[cubic-bezier(0.22,1,0.36,1)] lg:hover:grow-[2.6] lg:focus-within:grow-[2.6]"
    >
      <img
        src={intent.image}
        alt={intent.alt}
        loading="lazy"
        className="absolute inset-0 h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105"
      />
      <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-black/10" />

      <div className="relative flex h-full flex-col justify-end p-6 sm:p-8">
        <span className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-[var(--wf-clay)]">
          {intent.kicker}
        </span>
        <h3 className="mt-3 font-serif text-2xl font-semibold text-white sm:text-3xl [text-shadow:0_2px_18px_rgba(0,0,0,0.45)]">
          {intent.title}
        </h3>
        <div className="mt-2 overflow-hidden lg:max-h-0 lg:opacity-0 lg:transition-[max-height,opacity] lg:duration-500 lg:group-hover:max-h-40 lg:group-hover:opacity-100 lg:group-focus-within:max-h-40 lg:group-focus-within:opacity-100">
          <p className="max-w-sm text-sm leading-6 text-white/85">{intent.body}</p>
        </div>
        <a
          href={`#${intent.id}`}
          onClick={go}
          data-testid={`intent-cta-${intent.id}`}
          className="mt-5 inline-flex w-fit items-center gap-2 rounded-full bg-[var(--wf-paper)] px-5 py-2.5 text-sm font-semibold text-[var(--wf-maroon)] shadow-[var(--wf-shadow-sm)] transition-colors hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black/40"
        >
          {intent.cta}
          <ArrowRight className="h-4 w-4 motion-safe:transition-transform motion-safe:group-hover:translate-x-0.5" aria-hidden="true" />
        </a>
      </div>

      <span className="pointer-events-none absolute right-4 top-4 rounded-full bg-black/40 px-2.5 py-0.5 text-[10px] font-medium text-white/90 backdrop-blur-sm">
        {PHOTO_CREDIT.label}
      </span>
    </article>
  );
};

export const IntentsV2 = () => {
  const ordered = [
    INTENTS.find((i) => i.id === "where-to-buy"),
    INTENTS.find((i) => i.id === "support"),
    INTENTS.find((i) => i.id === "who-we-are"),
  ].filter(Boolean);
  return (
    <section aria-labelledby="intents-v2-heading" className="wf-section bg-[var(--wf-cream)]">
      <div className="wf-container">
        <Reveal className="max-w-2xl">
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[var(--wf-maroon)]">
            Choose your path
          </span>
          <h2 id="intents-v2-heading" className="mt-3 font-serif text-3xl font-semibold text-[var(--wf-ink)] sm:text-4xl lg:text-5xl">
            Three ways in. One community.
          </h2>
          <p className="mt-3 text-base leading-7 text-muted-foreground">
            Hover a panel to explore &mdash; or tap to follow your path.
          </p>
        </Reveal>

        <Reveal className="mt-10">
          <div className="flex flex-col gap-4 lg:flex-row">
            {ordered.map((intent) => (
              <Panel key={intent.id} intent={intent} />
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
